import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

//#region Material Components
import { withStyles } from '@material-ui/styles';
import MuiTable from '@material-ui/core/Table';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiIconButton from '@material-ui/core/IconButton';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import MoreIcon from '@material-ui/icons/MoreVert';
//#endregion

//#region Internal
import { Components, FontSize } from './../../assets/Dimensions';
import { Primary } from './../../assets/Colors';
//#endregion

// ─── Why this exists ─────────────────────────────────────────────────────────
// The shared <Grid> renders every row at once. The FeedBacks "Tüm Zamanlar"
// filter returns ~45k records, so mounting 45k <TableRow>s froze/crashed the tab.
// This drop-in keeps the same MUI table look + the data shape produced by
// util/getRows, but only renders the rows visible in the scroll viewport
// (plus a small overscan). The off-screen rows are replaced by two spacer rows
// whose heights reserve the exact scroll space, so the scrollbar stays accurate.
//
// Windowing math is exact only when every body row is the same height, so rows
// are clamped to ROW_HEIGHT (single line + ellipsis, full value on hover title).

//#region Constants
const ROW_HEIGHT = 40;         // px — fixed body-row height the math relies on
const OVERSCAN = 8;            // extra rows rendered above/below the viewport
const ACTION_COL_WIDTH = 80;   // left "⋮" action column
const COL_MIN_WIDTH = 150;     // per data column (drives minWidth + h-scroll)
const DEFAULT_VIEWPORT = 600;  // assumed height until the container is measured
//#endregion

//#region Override Style (mirrors components/Grid/index.js)
const Table = withStyles({
    root: {
        backgroundColor: 'white',
        borderRadius: 4,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.15)',
        borderCollapse: 'separate',
        tableLayout: 'fixed', // stable column widths while rows mount/unmount
    },
})(MuiTable);

const TableRow = withStyles({
    root: {
        '&:hover': {
            backgroundColor: '#f1ecec4a',
        },
    },
})(MuiTableRow);

const TableCell = withStyles({
    root: { padding: '4px' },
    body: { fontSize: FontSize.Small },
    head: { fontWeight: 'bold' },
})(MuiTableCell);

const IconButton = withStyles({
    label: {
        width: Components.Image.Huge.Width,
        height: Components.Image.Huge.Height,
    },
})(MuiIconButton);
//#endregion

//#region Inline Style
const ellipsis = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const stickyHeaderCell = {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    backgroundColor: Primary.ContainerBackground,
};

const spacerCell = { padding: 0, border: 0 };
//#endregion

const VirtualizedGrid = ({ data, options, selectedRowId, headerClick, rowClick }) => {
    const columns = (data && data.columns) || [];
    const rows = (data && data.rows) || [];
    const hasActions = typeof options === 'function';
    const colCount = columns.length + (hasActions ? 1 : 0);

    const scrollRef = useRef(null);
    const rafRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(DEFAULT_VIEWPORT);

    // Track the real viewport height so the visible-row count is correct on any
    // screen size / window resize.
    useLayoutEffect(() => {
        const measure = () => {
            if (scrollRef.current) setViewportHeight(scrollRef.current.clientHeight);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // A new result set (filter change / refetch swaps the rows array) → jump to
    // the top so the user isn't left scrolled into now-missing rows.
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        setScrollTop(0);
    }, [rows]);

    // Drop any pending frame on unmount.
    useEffect(
        () => () => {
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
        },
        []
    );

    // Coalesce scroll events to one state update per animation frame. Read from
    // the DOM node (not the synthetic event, which React 16 pools/nulls).
    const onScroll = () => {
        if (rafRef.current != null) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            if (scrollRef.current) setScrollTop(scrollRef.current.scrollTop);
        });
    };

    const total = rows.length;
    const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const visibleCount = Math.ceil(viewportHeight / ROW_HEIGHT) + OVERSCAN * 2;
    const endIndex = Math.min(total, startIndex + visibleCount);
    const topPad = startIndex * ROW_HEIGHT;
    const bottomPad = Math.max(0, (total - endIndex) * ROW_HEIGHT);
    const visibleRows = rows.slice(startIndex, endIndex);

    const tableMinWidth = ACTION_COL_WIDTH + columns.length * COL_MIN_WIDTH;

    return (
        <div
            ref={scrollRef}
            onScroll={onScroll}
            style={{
                height: 'calc(100vh - 240px)',
                minHeight: 300,
                overflow: 'auto',
                marginTop: 30,
            }}
        >
            <Table style={{ width: '100%', minWidth: tableMinWidth }}>
                <TableHead>
                    <MuiTableRow>
                        {hasActions && (
                            <TableCell style={{ ...stickyHeaderCell, width: ACTION_COL_WIDTH }} />
                        )}
                        {columns.map((col, headIndex) => (
                            <TableCell
                                key={headIndex}
                                onClick={() => headerClick && headerClick(col)}
                                title={col == null ? '' : String(col)}
                                style={{
                                    ...stickyHeaderCell,
                                    ...ellipsis,
                                    width: COL_MIN_WIDTH,
                                    cursor: headerClick ? 'pointer' : 'default',
                                }}
                            >
                                {col}
                            </TableCell>
                        ))}
                    </MuiTableRow>
                </TableHead>

                <TableBody>
                    {total === 0 ? (
                        <MuiTableRow>
                            <TableCell
                                colSpan={colCount || 1}
                                style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}
                            >
                                Veri bulunamadı.
                            </TableCell>
                        </MuiTableRow>
                    ) : (
                        <>
                            {topPad > 0 && (
                                <tr style={{ height: topPad }}>
                                    <td colSpan={colCount} style={spacerCell} />
                                </tr>
                            )}

                            {visibleRows.map((row, i) => {
                                const realIndex = startIndex + i;
                                const selected =
                                    selectedRowId != null && selectedRowId === realIndex;
                                return (
                                    <TableRow
                                        key={realIndex}
                                        id={row.data.id}
                                        onClick={(e) => rowClick && rowClick(e)}
                                        style={{
                                            height: ROW_HEIGHT,
                                            ...(selected ? { backgroundColor: '#e3f2fd' } : {}),
                                        }}
                                    >
                                        {hasActions && (
                                            <TableCell style={{ width: ACTION_COL_WIDTH }}>
                                                <IconButton
                                                    id={row.data.id}
                                                    style={{ padding: 0 }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        options(row.data, realIndex);
                                                    }}
                                                >
                                                    <MoreIcon
                                                        style={{
                                                            width: Components.Image.Big.Width,
                                                            height: Components.Image.Big.Height,
                                                        }}
                                                    />
                                                </IconButton>
                                            </TableCell>
                                        )}

                                        {row.data.content.map((cell, cellIndex) => (
                                            <TableCell
                                                key={cellIndex}
                                                title={cell.content == null ? '' : String(cell.content)}
                                                style={{ ...ellipsis, width: COL_MIN_WIDTH }}
                                            >
                                                {cell.content}
                                                {cell.component}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}

                            {bottomPad > 0 && (
                                <tr style={{ height: bottomPad }}>
                                    <td colSpan={colCount} style={spacerCell} />
                                </tr>
                            )}
                        </>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

export default VirtualizedGrid;
